import { useGLTF, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import React, { useEffect, useRef, Suspense, useMemo, Component } from "react";
import * as THREE from "three";


function Loader() {
  return (
    <mesh rotation={[0.4, 0.4, 0]}>
      <boxGeometry args={[0.5, 0.5, 0.5]} />
      <meshStandardMaterial color="hotpink" />
      {/* Add the Text component here.
        - position: Move the text relative to the mesh
        - fontSize: Adjust the size of the text
        - color: Set the text color
      */}
      <Text position={[0, 0, 0.5]} fontSize={0.2} color="white" anchorX="center" anchorY="middle">
        Loading...
      </Text>
    </mesh>
  );
}

const visemeMap = {
  // Bilabial consonants (lips together)
  p: "viseme_PP",
  b: "viseme_PP",
  m: "viseme_PP",
  
  // Alveolar consonants (tongue to roof of mouth)
  t: "viseme_PP",
  n: "viseme_PP",
  l: "viseme_PP",
  
  // Velar consonants (back of tongue)
  k: "viseme_kk",
  g: "viseme_kk",
  ng: "viseme_kk",
  
  // Labiodental consonants (lip to teeth)
  f: "viseme_FF",
  v: "viseme_FF",
  
  // Dental consonants (tongue between teeth)
  th: "viseme_TH",
  
  // Palato-alveolar consonants (tongue to hard palate)
  ch: "viseme_kk",
  j: "viseme_kk",
  sh: "viseme_kk",
  
  // Alveolar fricatives (tongue near roof)
  s: "viseme_TH",
  z: "viseme_TH",
  
  // Rhotic (R sound)
  r: "viseme_AA",
  
  // Vowels - front
  i: "viseme_I",
  y: "viseme_I",
  e: "viseme_I",
  eh: "viseme_I",
  
  // Vowels - central
  aa: "viseme_AA",
  ah: "viseme_AA",
  uh: "viseme_AA",
  er: "viseme_AA",
  
  // Vowels - back
  o: "viseme_O",
  u: "viseme_U",
  w: "viseme_U",
  
  // Silence
  sil: "SIL",
};

function preprocessVisemes(rawVisemes, minFrameMs = 50) {
  const schedule = [];
  let lastTime = -Infinity;
  let lastViseme = null;
  
  for (const v of rawVisemes) {
    if (v.type !== "viseme") continue;
    
    const viseme = visemeMap[v.value.toLowerCase()] || "SIL";
    const time = v.time / 1000; // Convert to seconds
    
    // Only skip if it's the same viseme and too close in time
    // This allows different visemes to be closer together for better sync
    if (viseme === lastViseme && time - lastTime < minFrameMs / 1000) {
      continue;
    }
    
    schedule.push({
      time: time,
      viseme: viseme,
      raw: v.value,
    });
    lastTime = time;
    lastViseme = viseme;
  }
  
  // Ensure we have at least a SIL at the start if schedule is empty
  if (schedule.length === 0 || schedule[0].viseme !== "SIL") {
    schedule.unshift({ time: 0, viseme: "SIL", raw: "sil" });
  }
  
  return schedule;
}

function TearParticles({ isCrying }) {
  const TEST_MODE = true;
  const particlesRef = useRef();
  const particleCount = 50;
  const velocitiesRef = useRef(new Float32Array(particleCount * 3));
  const lifetimesRef = useRef(new Float32Array(particleCount));
  const maxLifetime = 4.0;

  const tearGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const originX = i % 2 === 0 ? -0.3 : 0.3;
      const originY = 0.9;
      const originZ = 0.25;
      positions[i * 3] = originX + (Math.random() - 0.5) * 0.1;
      positions[i * 3 + 1] = originY + (Math.random() - 0.5) * 0.1;
      positions[i * 3 + 2] = originZ + (Math.random() - 0.5) * 0.1;
    }
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return geometry;
  }, []);

  const tearTexture = useMemo(() => {
    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const gradient = ctx.createRadialGradient(size / 2, size / 2, size * 0.1, size / 2, size / 2, size * 0.48);
    gradient.addColorStop(0, "rgba(255,255,255,0.95)");
    gradient.addColorStop(0.4, "rgba(255,0,0,0.8)");
    gradient.addColorStop(1, "rgba(160,0,0,0.8)");

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size * 0.48, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();

    const texture = new THREE.CanvasTexture(canvas);
    if (texture.colorSpace !== undefined) {
      texture.colorSpace = THREE.SRGBColorSpace;
    }
    texture.needsUpdate = true;
    return texture;
  }, []);

  const resetParticle = (positions, velocities, lifetimes, index) => {
    const originX = index % 2 === 0 ? -0.3 : 0.3;
    const originY = 0.9;
    const originZ = 0.25;
    positions.array[index * 3] = originX + (Math.random() - 0.5) * 0.08;
    positions.array[index * 3 + 1] = originY + (Math.random() - 0.5) * 0.08;
    positions.array[index * 3 + 2] = originZ + (Math.random() - 0.5) * 0.08;
    velocities[index * 3] = (Math.random() - 0.5) * 0.02;
    velocities[index * 3 + 1] = -0.05 - Math.random() * 0.05;
    velocities[index * 3 + 2] = (Math.random() - 0.5) * 0.015;
    lifetimes[index] = 0;
  };

  useEffect(() => {
    if (particlesRef.current) {
      particlesRef.current.visible = isCrying || TEST_MODE;
    }
  }, [isCrying]);

  useFrame((state, delta) => {
    if (!particlesRef.current || !particlesRef.current.visible || !tearGeometry) return;
    const positions = tearGeometry.attributes.position;
    const velocities = velocitiesRef.current;
    const lifetimes = lifetimesRef.current;

    for (let i = 0; i < particleCount; i++) {
      lifetimes[i] += delta;
      if (lifetimes[i] > maxLifetime * 0.6 || positions.array[i * 3 + 1] < -1.5) {
        resetParticle(positions, velocities, lifetimes, i);
      } else {
        positions.array[i * 3] += velocities[i * 3];
        positions.array[i * 3 + 1] += velocities[i * 3 + 1];
        positions.array[i * 3 + 2] += velocities[i * 3 + 2];
        velocities[i * 3 + 1] -= 0.0003;
      }
    }

    positions.needsUpdate = true;
  });

  return (
    <points ref={particlesRef} visible={isCrying || TEST_MODE} frustumCulled={false} renderOrder={1000}>
      <primitive object={tearGeometry} attach="geometry" />
      <pointsMaterial
        color={TEST_MODE ? "#FF0000" : "#87CEEB"}
        size={TEST_MODE ? 1.4 : 0.3}
        map={tearTexture}
        alphaMap={tearTexture}
        transparent={true}
        opacity={1.0}
        sizeAttenuation={true}
        depthWrite={false}
        depthTest={false}
        blending={THREE.NormalBlending}
      />
    </points>
  );
}

// Fallback component when avatar fails to load
function AvatarFallback({ avatarId, ...props }) {
  return (
    <group {...props}>
      <mesh>
        <boxGeometry args={[1, 2, 0.5]} />
        <meshStandardMaterial color="#4a5568" />
      </mesh>
      <Text
        position={[0, 1.5, 0]}
        fontSize={0.3}
        color="white"
        anchorX="center"
        anchorY="middle"
        maxWidth={2}
      >
        Avatar unavailable
      </Text>
      <Text
        position={[0, 1.2, 0]}
        fontSize={0.15}
        color="#a0aec0"
        anchorX="center"
        anchorY="middle"
        maxWidth={2}
      >
        Network blocked or model unavailable
      </Text>
    </group>
  );
}

// Error boundary component for avatar loading
class AvatarErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    console.warn("[Avatar] getDerivedStateFromError called:", error);
    // Always catch avatar loading errors - don't re-throw
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging
    console.warn("[Avatar] Failed to load avatar model:", error);
    console.warn("[Avatar] Error info:", errorInfo);
  }

  componentDidMount() {
    // Also listen for unhandled promise rejections (async errors from fetch)
    this.errorHandler = (event) => {
      if (event.reason && (
        (event.reason.message && event.reason.message.includes('readyplayer.me')) ||
        (event.reason.message && event.reason.message.includes('Failed to fetch')) ||
        (typeof event.reason === 'string' && event.reason.includes('readyplayer.me'))
      )) {
        console.warn("[Avatar] Caught unhandled error for avatar:", event.reason);
        this.setState({ hasError: true, error: event.reason });
        event.preventDefault(); // Prevent default error handling
      }
    };
    window.addEventListener('unhandledrejection', this.errorHandler);
  }

  componentWillUnmount() {
    if (this.errorHandler) {
      window.removeEventListener('unhandledrejection', this.errorHandler);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      console.log("[Avatar] Rendering fallback UI due to error");
      return <AvatarFallback avatarId={this.props.avatarId} {...this.props.avatarProps} />;
    }

    return this.props.children;
  }
}

function AvatarModel({ avatarId, speechMarks, isCrying = false, ...props }) {
  console.log("[AvatarModel] Rendering with isCrying:", isCrying);
  const url = `https://models.readyplayer.me/${avatarId}.glb?morphTargets=ARKit,Oculus%20Visemes`;
  
  // useGLTF will suspend on load, and throw errors that ErrorBoundary can catch
  // If the fetch fails, it will throw an error that the ErrorBoundary will catch
  let nodes, materials;
  try {
    const result = useGLTF(url);
    nodes = result.nodes;
    materials = result.materials;
  } catch (error) {
    // This won't catch async errors, but ErrorBoundary will handle them
    throw error;
  }

  const group = useRef();
  const blinkLeftIndex = useRef();
  const blinkRightIndex = useRef();
  const blinkTimer = useRef(0);
  const blinkProgress = useRef(0);

  const scheduleRef = useRef([]);
  const startTimeRef = useRef(null);
  const currentVisemeRef = useRef("SIL");
  const timingOffsetRef = useRef(0); // Offset to account for audio delay (in seconds)
  const visemeSequenceIdRef = useRef(0); // Track viseme sequence to handle multiple calls
  const pendingStartTimeRef = useRef(null); // Store pending start time until audio starts
  
  useEffect(() => {
    if (speechMarks?.length) {
      console.log(`[Avatar] Received ${speechMarks.length} speech marks, preprocessing for lip sync`);
      const processed = preprocessVisemes(speechMarks);
      console.log(`[Avatar] Processed ${processed.length} viseme events for lip sync`);
      
      // Increment sequence ID for this new viseme set
      visemeSequenceIdRef.current += 1;
      const currentSequenceId = visemeSequenceIdRef.current;
      console.log(`[Avatar] New viseme sequence ID: ${currentSequenceId}`);
      
      scheduleRef.current = processed;
      // Small negative offset to account for any audio buffering/startup delay
      timingOffsetRef.current = -0.05; // 50ms offset to sync with audio
      
      // Reset current viseme to SIL when new visemes arrive
      currentVisemeRef.current = "SIL";
      
      // Don't set start time yet - wait for audio to actually start
      // This ensures proper sync for multiple sequential TTS calls
      startTimeRef.current = null;
      pendingStartTimeRef.current = performance.now() / 1000;
      console.log(`[Avatar] Waiting for audio start event for sequence ${currentSequenceId}`);
      
      // Listen for audio start event to sync properly
      const handleAudioStart = (event) => {
        const audioStartTime = event.detail?.timestamp || performance.now() / 1000;
        // Only sync if this is still the current sequence
        if (currentSequenceId === visemeSequenceIdRef.current) {
          // Set start time to when audio actually started
          startTimeRef.current = audioStartTime;
          pendingStartTimeRef.current = null;
          console.log(`[Avatar] ✅ Start time synced with audio for sequence ${currentSequenceId} at ${startTimeRef.current}`);
        } else {
          console.log(`[Avatar] ⚠️ Ignoring audio start event for old sequence (current: ${visemeSequenceIdRef.current}, event: ${currentSequenceId})`);
        }
      };
      
      window.addEventListener('tts-audio-started', handleAudioStart);
      
      // Fallback: if audio doesn't start within 500ms, use current time
      const fallbackTimeout = setTimeout(() => {
        if (currentSequenceId === visemeSequenceIdRef.current && !startTimeRef.current) {
          startTimeRef.current = performance.now() / 1000;
          pendingStartTimeRef.current = null;
          console.log(`[Avatar] ⚠️ Fallback: Start time set for sequence ${currentSequenceId} at ${startTimeRef.current} (audio start event didn't fire)`);
        }
      }, 500);
      
      return () => {
        window.removeEventListener('tts-audio-started', handleAudioStart);
        clearTimeout(fallbackTimeout);
      };
    } else {
      console.warn("[Avatar] No speech marks received or empty array");
      scheduleRef.current = [];
      startTimeRef.current = null;
      pendingStartTimeRef.current = null;
      timingOffsetRef.current = 0;
    }
  }, [speechMarks]);

  useEffect(() => {
    blinkLeftIndex.current =
      nodes.Wolf3D_Head?.morphTargetDictionary?.["eyeBlinkLeft"];
    blinkRightIndex.current =
      nodes.Wolf3D_Head?.morphTargetDictionary?.["eyeBlinkRight"];

  }, [nodes]);

  const mouthOpenIndex = useRef();
  const mouthFrownIndex = useRef();

  useEffect(() => {
    const dict = nodes.Wolf3D_Head?.morphTargetDictionary || {};
    // For crying, use viseme_I to open the mouth like saying "I"
    mouthOpenIndex.current = dict["viseme_kk"];
    mouthFrownIndex.current = dict["mouthFrown"] ?? dict["mouthSad"] ?? dict["jawDrop"];
  }, [nodes]);

  useFrame((state, delta) => {
    const now = performance.now() / 1000;
    const start = startTimeRef.current ?? now;
    // Apply timing offset to sync with audio playback
    const elapsed = (now - start) + timingOffsetRef.current;
    const schedule = scheduleRef.current;

    // Find current and next viseme with smooth blending
    let currentViseme = "SIL";
    let nextViseme = null;
    let blendFactor = 0;
    const transitionDuration = 0.3; // 300ms transition between visemes (increased for slower, more natural movement)

    if (schedule.length) {
      const lastVisemeTime = schedule[schedule.length - 1]?.time ?? 0;
      
      // If we're past the last viseme, return to SIL
      if (elapsed > lastVisemeTime + 0.2) {
        currentViseme = "SIL";
        nextViseme = null;
        blendFactor = 0;
      } else {
        // Find current viseme in schedule
        for (let i = 0; i < schedule.length; i++) {
          const { time, viseme } = schedule[i];
          const nextTime = schedule[i + 1]?.time ?? Infinity;
          
          if (elapsed >= time && elapsed < nextTime) {
            currentViseme = viseme;
            nextViseme = schedule[i + 1]?.viseme || null;
            
            // Calculate blend factor for smooth transition to next viseme
            if (nextViseme && nextTime !== Infinity) {
              const timeUntilNext = nextTime - elapsed;
              blendFactor = Math.max(0, Math.min(1, 1 - (timeUntilNext / transitionDuration)));
            }
            break;
          }
        }
      }
    }

    currentVisemeRef.current = currentViseme;

    // Apply visemes with smooth blending
    Object.values(visemeMap).forEach((viseme) => {
      if (viseme === "SIL") return;
      
      const idxHead = nodes.Wolf3D_Head?.morphTargetDictionary?.[viseme];
      const idxTeeth = nodes.Wolf3D_Teeth?.morphTargetDictionary?.[viseme];
      
      // Calculate target influence: 1.0 for current, blendFactor for next, 0 for others
      let targetInfluence = 0;
      if (viseme === currentViseme) {
        targetInfluence = 1.0 - blendFactor; // Fade out current viseme as next approaches
      } else if (viseme === nextViseme && nextViseme !== null) {
        targetInfluence = blendFactor; // Fade in next viseme
      }
      
      // Slower interpolation for smoother, more natural lip movement (reduced for slower animation)
      const lerpSpeed = 0.15;
      
      if (idxHead !== undefined) {
        const currentValue = nodes.Wolf3D_Head.morphTargetInfluences[idxHead] || 0;
        nodes.Wolf3D_Head.morphTargetInfluences[idxHead] = THREE.MathUtils.lerp(
          currentValue,
          targetInfluence,
          lerpSpeed
        );
      }
      if (idxTeeth !== undefined) {
        const currentValue = nodes.Wolf3D_Teeth.morphTargetInfluences[idxTeeth] || 0;
        nodes.Wolf3D_Teeth.morphTargetInfluences[idxTeeth] = THREE.MathUtils.lerp(
          currentValue,
          targetInfluence,
          lerpSpeed
        );
      }
    });

    group.current?.getObjectByName("Head")?.lookAt(state.camera.position);

    if (isCrying) {
      // Force eyes closed
      if (blinkLeftIndex.current !== undefined) {
        nodes.Wolf3D_Head.morphTargetInfluences[blinkLeftIndex.current] = 1;
      }
      if (blinkRightIndex.current !== undefined) {
        nodes.Wolf3D_Head.morphTargetInfluences[blinkRightIndex.current] = 1;
      }
      // Open mouth like saying viseme_I for crying
      if (mouthOpenIndex.current !== undefined) {
        nodes.Wolf3D_Head.morphTargetInfluences[mouthOpenIndex.current] = 0.7;
      }
      // Add slight frown for sad/crying expression
      if (mouthFrownIndex.current !== undefined) {
        nodes.Wolf3D_Head.morphTargetInfluences[mouthFrownIndex.current] = 0.5;
      }
    } else {
      blinkTimer.current += delta;
      if (blinkTimer.current > 3 + Math.random() * 3) {
        blinkTimer.current = 0;
        blinkProgress.current = 1;
      }

      if (blinkProgress.current > 0) {
        blinkProgress.current = Math.max(0, blinkProgress.current - delta * 4);
        if (blinkLeftIndex.current !== undefined) {
          nodes.Wolf3D_Head.morphTargetInfluences[blinkLeftIndex.current] =
            blinkProgress.current;
        }
        if (blinkRightIndex.current !== undefined) {
          nodes.Wolf3D_Head.morphTargetInfluences[blinkRightIndex.current] =
            blinkProgress.current;
        }
      }
    }

    if (!isCrying) {
      // Smoothly close mouth if it was forced open for crying
      const currentVisemeIdx =
        nodes.Wolf3D_Head?.morphTargetDictionary?.[currentVisemeRef.current];
      if (mouthOpenIndex.current !== undefined && mouthOpenIndex.current !== currentVisemeIdx) {
        const currentValue = nodes.Wolf3D_Head.morphTargetInfluences[mouthOpenIndex.current] ?? 0;
        nodes.Wolf3D_Head.morphTargetInfluences[mouthOpenIndex.current] = THREE.MathUtils.lerp(
          currentValue,
          0,
          0.2
        );
      }
      if (mouthFrownIndex.current !== undefined) {
        const currentValue = nodes.Wolf3D_Head.morphTargetInfluences[mouthFrownIndex.current] ?? 0;
        nodes.Wolf3D_Head.morphTargetInfluences[mouthFrownIndex.current] = THREE.MathUtils.lerp(
          currentValue,
          0,
          0.2
        );
      }
    }
});

  const safePrimitive = (object) => (object ? <primitive object={object} /> : null);
  const safeSkinned = (nodeKey, materialKey, extraProps = {}) => {
    const node = nodes?.[nodeKey];
    const material = materials?.[materialKey];
    if (!node?.geometry || !material) return null;
    return (
      <skinnedMesh
        geometry={node.geometry}
        material={material}
        skeleton={node.skeleton}
        morphTargetDictionary={node.morphTargetDictionary}
        morphTargetInfluences={node.morphTargetInfluences}
        {...extraProps}
      />
    );
  };

  return (
    <group {...props} dispose={null} ref={group}>
      {safePrimitive(nodes?.Hips)}
      {safeSkinned("Wolf3D_Body", "Wolf3D_Body")}
      {safeSkinned("Wolf3D_Outfit_Bottom", "Wolf3D_Outfit_Bottom")}
      {safeSkinned("Wolf3D_Outfit_Footwear", "Wolf3D_Outfit_Footwear")}
      {safeSkinned("Wolf3D_Outfit_Top", "Wolf3D_Outfit_Top")}
      {safeSkinned("Wolf3D_Hair", "Wolf3D_Hair")}
      {safeSkinned("EyeLeft", "Wolf3D_Eye", { name: "EyeLeft" })}
      {safeSkinned("EyeRight", "Wolf3D_Eye", { name: "EyeRight" })}
      {safeSkinned("Wolf3D_Head", "Wolf3D_Skin", { name: "Wolf3D_Head" })}
      {safeSkinned("Wolf3D_Teeth", "Wolf3D_Teeth", { name: "Wolf3D_Teeth" })}
      <TearParticles isCrying={isCrying} />
    </group>
  );
}

// Wrapper component that catches errors before they propagate
function SafeAvatarModel(props) {
  const [hasError, setHasError] = React.useState(false);

  // Catch errors early to prevent React error overlay
  React.useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    // Intercept console errors related to avatar loading
    console.error = (...args) => {
      const errorStr = args.join(' ');
      if (errorStr.includes('readyplayer.me') || 
          errorStr.includes('Failed to fetch') ||
          errorStr.includes('ERR_CERT_AUTHORITY_INVALID') ||
          errorStr.includes('Could not load')) {
        console.warn("[Avatar] Suppressed avatar loading error:", ...args);
        setHasError(true);
        return; // Don't log the error
      }
      originalError.apply(console, args);
    };

    const handleError = (event) => {
      if (event.error && (
        event.error.message?.includes('readyplayer.me') ||
        event.error.message?.includes('Failed to fetch') ||
        event.error.message?.includes('ERR_CERT_AUTHORITY_INVALID')
      )) {
        console.warn("[Avatar] Caught error in SafeAvatarModel:", event.error);
        setHasError(true);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      const errorMessage = reason?.message || String(reason || '');
      if (errorMessage.includes('readyplayer.me') || 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('ERR_CERT_AUTHORITY_INVALID') ||
          errorMessage.includes('Could not load')) {
        console.warn("[Avatar] Caught unhandled rejection in SafeAvatarModel:", reason);
        setHasError(true);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    window.addEventListener('error', handleError, true); // Use capture phase
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (hasError) {
    return <AvatarFallback avatarId={props.avatarId} {...props} />;
  }

  return <AvatarModel {...props} />;
}

// Avatar wrapper with comprehensive error handling
export function Avatar(props) {
  return (
    <AvatarErrorBoundary avatarId={props.avatarId} avatarProps={props}>
      <Suspense fallback={<Loader />}>
        <SafeAvatarModel {...props} />
      </Suspense>
    </AvatarErrorBoundary>
  );
}
